(function () {
  const DATA_ATTR = 'data-subscription-sale-applied';
  const ORIGINAL_SUBSCRIPTION_ATTR = 'data-subscription-sale-original-cents';
  const ORIGINAL_RETAIL_ATTR = 'data-subscription-sale-retail-cents';
  const LINE_QTY_ATTR = 'data-subscription-sale-line-qty';
  const ORIGINAL_ONE_TIME_PRICE_ATTR = 'data-labor-day-original-onetime-price';
  const PERCENT_PATTERN = /(\d+(?:\.\d+)?)\s*%/;
  const DRAWER_BENEFITS_SAVE_PATTERN = /^Save\s+\d+(?:\.\d+)?%\s+on\b/i;
  const DRAWER_BENEFITS_COMPLETE_PATTERN =
    /^Save\s+\d+(?:\.\d+)?%\s+on\s+first order,\s*20%\s+off\s+on\s+subsequent orders$/i;

  const state = {
    config: null,
    pdpObservers: new WeakMap(),
    drawerObservers: new WeakMap(),
    drawerPricingByVariant: {},
    lockedDrawerPricing: null,
    updateTimer: null,
    drawerTimer: null,
    drawerUpdateLocked: false,
    qtyUpdateTimer: null,
    drawerCopyTimer: null,
    pdpCopyTimer: null,
    drawerImmediateRunning: false,
  };

  function parseConfig() {
    const merged = {
      enabled: false,
      extraDiscountPercent: 0,
      laborDayActive: false,
      laborDayDiscountPercent: 0,
      variants: {},
    };

    document.querySelectorAll('[id^="SubscriptionSalePricing-"]').forEach((configEl) => {
      try {
        const data = JSON.parse(configEl.textContent);
        if (data.enabled) merged.enabled = true;
        if (typeof data.extraDiscountPercent === 'number') {
          merged.extraDiscountPercent = data.extraDiscountPercent;
        }
        if (data.laborDayActive) merged.laborDayActive = true;
        if (typeof data.laborDayDiscountPercent === 'number') {
          merged.laborDayDiscountPercent = data.laborDayDiscountPercent;
        }
        if (data.variants) {
          Object.assign(merged.variants, data.variants);
        }
      } catch (error) {
        console.error('Subscription sale pricing config is invalid.', error);
      }
    });

    return merged.enabled ? merged : null;
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    return `$${(cents / 100).toFixed(2)}`;
  }

  function parseMoneyToCents(value) {
    const match = String(value || '').match(/\$?\s*([\d,]+(?:\.\d{1,2})?)/);
    if (!match) return 0;

    return Math.round(parseFloat(match[1].replace(/,/g, '')) * 100);
  }

  function formatSavingsPercent(savingsPercent) {
    if (!savingsPercent || savingsPercent <= 0) return '0';
    return String(Math.round(Number(savingsPercent)));
  }

  function calculatePricing(retailCents, subscriptionCents, extraDiscountPercent, laborDayActive, laborDayDiscountPercent) {
    if (!subscriptionCents || subscriptionCents <= 0) return null;

    let saleCents = subscriptionCents;
    if (laborDayActive && laborDayDiscountPercent > 0) {
      // Compounding: Labor Day is taken off the already standing-discounted subscription price,
      // not off retail. This can never go negative since it's a percentage of a positive number.
      const laborDayDiscountCents = Math.floor((subscriptionCents * laborDayDiscountPercent) / 100);
      saleCents = subscriptionCents - laborDayDiscountCents;
    } else if (extraDiscountPercent > 0) {
      const discountCents = Math.floor((subscriptionCents * extraDiscountPercent) / 100);
      saleCents = subscriptionCents - discountCents;
    }

    let savingsPercent = 0;
    if (retailCents > saleCents && saleCents > 0) {
      savingsPercent = Math.round(((retailCents - saleCents) * 100) / retailCents);
    }

    return {
      retailCents,
      subscriptionCents,
      saleCents,
      savingsPercent,
    };
  }

  function getPricing(config, variantId) {
    if (!config?.enabled || !variantId) return null;

    const cached = config.variants[String(variantId)];
    if (cached?.saleCents > 0) {
      return cached;
    }

    return null;
  }

  function mergeConfig() {
    const fresh = parseConfig();
    if (!fresh || !state.config) return;
    state.config.enabled = fresh.enabled;
    state.config.extraDiscountPercent = fresh.extraDiscountPercent;
    state.config.laborDayActive = fresh.laborDayActive;
    state.config.laborDayDiscountPercent = fresh.laborDayDiscountPercent;
    Object.assign(state.config.variants, fresh.variants);
  }

  function getCompareCentsNearPrice(priceElement) {
    const compareSelectors =
      '[part="money"][class*="line-through"], [part="money"].line-through, .line-through, s, del, [part*="compare"]';
    let scope = priceElement;

    for (let depth = 0; depth < 12 && scope; depth += 1) {
      for (const element of collectElements(compareSelectors, scope)) {
        if (priceElement === element || priceElement.contains?.(element)) continue;
        const cents = parseMoneyToCents(element.textContent);
        if (cents > 0) return cents;
      }
      scope = getParentElement(scope);
    }

    return 0;
  }

  function readVisibleQuantityFromScope(scope) {
    if (!scope?.querySelectorAll) return 0;

    const qtyContainers = collectElements(
      '[part*="quantity"], [class*="quantity"], [class*="qty"]',
      scope
    );

    for (const container of qtyContainers) {
      const qty = parseQuantityFromContainer(container);
      if (qty > 0) return qty;
    }

    const buttons = collectElements('button', scope);
    for (const button of buttons) {
      const stepper = button.parentElement;
      if (!stepper) continue;

      const stepperButtons = stepper.querySelectorAll?.('button');
      if (!stepperButtons || stepperButtons.length !== 2) continue;

      const qty = parseQuantityFromContainer(stepper);
      if (qty > 0) return qty;
    }

    return 0;
  }

  function findLineContainer(priceElement) {
    let scope = priceElement;

    for (let depth = 0; depth < 16 && scope; depth += 1) {
      const hasCompare = getCompareCentsNearPrice(scope) > 0;
      const hasQty =
        readVisibleQuantityFromScope(scope) > 0 ||
        collectElements('input[type="number"], input[inputmode="numeric"], [role="spinbutton"]', scope).length > 0;

      if (hasCompare && hasQty) return scope;
      scope = getParentElement(scope);
    }

    return priceElement;
  }

  function clearDrawerPriceStamps(priceEl) {
    priceEl.removeAttribute(DATA_ATTR);
    priceEl.removeAttribute(ORIGINAL_SUBSCRIPTION_ATTR);
    priceEl.removeAttribute(ORIGINAL_RETAIL_ATTR);
    priceEl.removeAttribute(LINE_QTY_ATTR);
  }

  function clearStaleDrawerStamps(priceEl) {
    const stampedRetail = parseInt(priceEl.getAttribute(ORIGINAL_RETAIL_ATTR), 10);
    const compareCents = getCompareCentsNearPrice(priceEl);
    const qty = getLineQuantity(priceEl, stampedRetail || compareCents) || 1;
    const currentCents = parseMoneyToCents(priceEl.textContent);

    if (compareCents > 0 && stampedRetail > 0) {
      const expectedLineCompare = stampedRetail * qty;
      if (Math.abs(compareCents - expectedLineCompare) > Math.max(qty * 2, 5)) {
        clearDrawerPriceStamps(priceEl);
        return;
      }
    }

    if (compareCents > 0 && currentCents > compareCents) {
      clearDrawerPriceStamps(priceEl);
    }
  }

  function getUnitRetailFromCompare(priceEl) {
    const lineCompare = getCompareCentsNearPrice(priceEl);
    if (!lineCompare) return 0;

    const qty = getLineQuantity(priceEl, lineCompare) || 1;
    if (qty > 1) {
      const unitRetail = Math.round(lineCompare / qty);
      if (unitRetail > 0 && Math.abs(lineCompare - unitRetail * qty) <= Math.max(qty * 2, 5)) {
        return unitRetail;
      }
    }

    return lineCompare;
  }

  function resolvePricingForPriceElement(priceEl, config) {
    try {
      clearStaleDrawerStamps(priceEl);

      const lineRoot = findLineContainer(priceEl);
      const variantId = getDrawerVariantId(lineRoot) || getDrawerVariantId(priceEl);
      let retailCents = getUnitRetailFromCompare(priceEl);

      if (variantId && config.variants[String(variantId)]?.subscriptionCents) {
        const variant = config.variants[String(variantId)];
        if (!retailCents || retailCents === variant.retailCents) {
          return calculatePricing(
            variant.retailCents,
            variant.subscriptionCents,
            config.extraDiscountPercent,
            config.laborDayActive,
            config.laborDayDiscountPercent
          );
        }
      }

      if (variantId) {
        const formPricing = getPricingFromActiveForm(config, variantId);
        if (formPricing?.saleCents && retailCents === formPricing.retailCents) {
          return formPricing;
        }
      }

      if (!retailCents) {
        retailCents = readOriginalRetailCents(lineRoot, 0);
      }

      let subscriptionCents = parseInt(priceEl.getAttribute(ORIGINAL_SUBSCRIPTION_ATTR), 10) || 0;
      if (!subscriptionCents || subscriptionCents >= retailCents) {
        clearDrawerPriceStamps(priceEl);
        const rechargeCents = parseMoneyToCents(priceEl.textContent);
        if (rechargeCents > 0 && retailCents > 0 && rechargeCents < retailCents) {
          subscriptionCents = rechargeCents;
        }
      }

      if (!subscriptionCents || !retailCents || retailCents <= subscriptionCents) return null;

      return calculatePricing(
        retailCents,
        subscriptionCents,
        config.extraDiscountPercent,
        config.laborDayActive,
        config.laborDayDiscountPercent
      );
    } catch (error) {
      console.error('Subscription sale drawer pricing failed for a line item.', error);
      return null;
    }
  }

  function storeDrawerPricing(variantId, pricing) {
    if (!pricing?.saleCents) return;

    const resolvedVariantId = variantId || getActiveVariantId();
    if (resolvedVariantId) {
      state.drawerPricingByVariant[String(resolvedVariantId)] = pricing;
      storePricing(state.config, resolvedVariantId, pricing);
      return;
    }

    state.lockedDrawerPricing = pricing;
  }

  function getStoredDrawerPricing(config, variantId) {
    const resolvedVariantId = variantId || getActiveVariantId();
    if (resolvedVariantId) {
      return (
        getPricing(config, resolvedVariantId) ||
        state.drawerPricingByVariant[String(resolvedVariantId)] ||
        null
      );
    }

    return state.lockedDrawerPricing;
  }

  function rememberOriginalMoney(element, attributeName, cents) {
    if (!element || !cents) return cents;
    if (!element.getAttribute(attributeName)) {
      element.setAttribute(attributeName, String(cents));
    }
    return parseInt(element.getAttribute(attributeName), 10) || cents;
  }

  function readOriginalSubscriptionCents(elements, fallbackSubscriptionCents) {
    let subscriptionCents = fallbackSubscriptionCents || 0;

    elements.forEach((element) => {
      const stored = parseInt(element.getAttribute(ORIGINAL_SUBSCRIPTION_ATTR), 10);
      if (stored > 0) {
        subscriptionCents = Math.max(subscriptionCents, stored);
        return;
      }

      if (element.hasAttribute(DATA_ATTR)) return;

      const cents = parseMoneyToCents(element.textContent);
      if (cents <= 0) return;
      if (subscriptionCents > 0 && cents < subscriptionCents) return;

      rememberOriginalMoney(element, ORIGINAL_SUBSCRIPTION_ATTR, cents);
      subscriptionCents = Math.max(subscriptionCents, cents);
    });

    return subscriptionCents;
  }

  function readOriginalRetailCents(root, subscriptionCents) {
    let retailCents = 0;
    const retailSelectors = ['.line-through', 's', 'del', '[part*="money compare"]', '[part*="money original"]'];

    retailSelectors.forEach((selector) => {
      root.querySelectorAll?.(selector).forEach((element) => {
        const stored = parseInt(element.getAttribute(ORIGINAL_RETAIL_ATTR), 10);
        if (stored > subscriptionCents && stored > retailCents) {
          retailCents = stored;
          return;
        }

        if (element.hasAttribute(DATA_ATTR)) return;

        const cents = parseMoneyToCents(element.textContent);
        if (cents <= subscriptionCents) return;

        rememberOriginalMoney(element, ORIGINAL_RETAIL_ATTR, cents);
        if (cents > retailCents) retailCents = cents;
      });
    });

    queryDeep(retailSelectors.join(','), root).forEach((element) => {
      const stored = parseInt(element.getAttribute(ORIGINAL_RETAIL_ATTR), 10);
      if (stored > subscriptionCents && stored > retailCents) {
        retailCents = stored;
        return;
      }

      if (element.hasAttribute(DATA_ATTR)) return;

      const cents = parseMoneyToCents(element.textContent);
      if (cents <= subscriptionCents) return;

      rememberOriginalMoney(element, ORIGINAL_RETAIL_ATTR, cents);
      if (cents > retailCents) retailCents = cents;
    });

    return retailCents;
  }

  function getParentElement(element) {
    if (!element) return null;
    if (element.parentElement) return element.parentElement;
    const root = element.getRootNode?.();
    if (root?.host) return root.host;
    return null;
  }

  function collectElements(selector, scope) {
    const seen = new Set();
    const results = [];

    scope?.querySelectorAll?.(selector)?.forEach((element) => {
      seen.add(element);
      results.push(element);
    });

    queryDeep(selector, scope).forEach((element) => {
      if (seen.has(element)) return;
      seen.add(element);
      results.push(element);
    });

    return results;
  }

  function parseQuantityFromContainer(container) {
    if (!container) return 0;

    const inputs = collectElements(
      'input[type="number"], input[inputmode="numeric"], [role="spinbutton"]',
      container
    );

    for (const input of inputs) {
      const ariaQty = parseInt(input.getAttribute('aria-valuenow'), 10);
      if (ariaQty >= 1) return ariaQty;

      const valueQty = parseInt(input.value, 10);
      if (valueQty >= 1) return valueQty;

      const textQty = parseInt(input.textContent?.trim(), 10);
      if (textQty >= 1) return textQty;
    }

    const text = container.textContent?.replace(/\s+/g, ' ').trim() || '';
    if (!text || text.length > 24) return 0;

    const stepperMatch = text.match(/(?:^|[−–\-+\u2212]\s*)(\d{1,2})(?:\s*[−–\-+\u2212]|$)/);
    if (stepperMatch) {
      const qty = parseInt(stepperMatch[1], 10);
      if (qty >= 1) return qty;
    }

    if (/^\d{1,2}$/.test(text)) {
      return parseInt(text, 10);
    }

    return 0;
  }

  function getLineQuantityFromStepper(priceElement) {
    let scope = priceElement;

    for (let depth = 0; depth < 16 && scope; depth += 1) {
      const qtyContainers = collectElements(
        '[part*="quantity"], [class*="quantity"], [class*="qty"]',
        scope
      );

      for (const container of qtyContainers) {
        const qty = parseQuantityFromContainer(container);
        if (qty >= 1) return qty;
      }

      const inputs = collectElements('input[type="number"], input[inputmode="numeric"], [role="spinbutton"]', scope);
      if (inputs.length === 1) {
        const qty = parseQuantityFromContainer(inputs[0]);
        if (qty >= 1) return qty;
      }

      const buttons = collectElements('button', scope);
      for (const button of buttons) {
        const stepper = button.parentElement;
        if (!stepper) continue;

        const stepperButtons = stepper.querySelectorAll?.('button');
        if (!stepperButtons || stepperButtons.length !== 2) continue;

        const qty = parseQuantityFromContainer(stepper);
        if (qty >= 1) return qty;
      }

      scope = getParentElement(scope);
    }

    return 0;
  }

  function inferQuantityFromCompare(priceElement, unitRetailCents) {
    if (!unitRetailCents) return 0;

    let scope = priceElement;
    for (let depth = 0; depth < 14 && scope; depth += 1) {
      const compareSelectors =
        '[part="money"][class*="line-through"], [part="money"].line-through, .line-through, s, del, [part*="compare"]';
      let bestQty = 0;

      collectElements(compareSelectors, scope).forEach((element) => {
        if (element === priceElement || priceElement.contains?.(element)) return;

        const compareCents = parseMoneyToCents(element.textContent);
        if (compareCents <= unitRetailCents) return;

        const inferredQty = Math.round(compareCents / unitRetailCents);
        if (
          inferredQty >= 2 &&
          inferredQty <= 99 &&
          Math.abs(compareCents - unitRetailCents * inferredQty) <= Math.max(inferredQty * 2, 10)
        ) {
          bestQty = Math.max(bestQty, inferredQty);
        }
      });

      if (bestQty > 0) return bestQty;
      scope = getParentElement(scope);
    }

    return 0;
  }

  function getLineQuantity(priceElement, unitRetailCents) {
    const stepperQty = getLineQuantityFromStepper(priceElement);
    if (stepperQty > 0) return stepperQty;

    const compareQty = inferQuantityFromCompare(priceElement, unitRetailCents);
    return compareQty > 0 ? compareQty : 1;
  }

  function setTextIfChanged(element, nextText) {
    if (!element || element.textContent.trim() === nextText) {
      if (element) element.setAttribute(DATA_ATTR, 'true');
      return;
    }

    element.textContent = nextText;
    element.setAttribute(DATA_ATTR, 'true');
  }

  function replacePercentInText(text, savingsPercent) {
    if (!text || savingsPercent <= 0 || !PERCENT_PATTERN.test(text)) return null;
    return text.replace(PERCENT_PATTERN, `${formatSavingsPercent(savingsPercent)}%`);
  }

  function isSubscriptionPlanLabel(text) {
    if (!text || text.length > 160) return false;

    const normalized = text.trim();
    if (!PERCENT_PATTERN.test(normalized)) return false;

    return (
      /subscription/i.test(normalized) ||
      /deliver(?:y)?\s+every/i.test(normalized) ||
      /:\s*save\b/i.test(normalized) ||
      /every\s+\d+\s*(?:day|days|week|weeks|month|months|year|years)/i.test(normalized)
    );
  }

  function updateSellingPlanOptionsInSelect(select, savingsPercent) {
    if (!select) return;

    select.querySelectorAll('option').forEach((option) => {
      const currentText = (option.label || option.textContent || '').trim();
      if (!isSubscriptionPlanLabel(currentText)) return;

      const nextText = replacePercentInText(currentText, savingsPercent);
      if (!nextText || currentText === nextText) {
        option.setAttribute(DATA_ATTR, 'true');
        return;
      }

      setOptionLabel(option, nextText);
    });
  }

  function updateDeepestDrawerText(root, selectors, matchFn, transformFn) {
    const candidates = [];
    const seen = new Set();

    const visit = (element) => {
      if (!element || seen.has(element)) return;
      seen.add(element);

      const currentText = element.textContent?.trim();
      if (!currentText || !matchFn(currentText)) return;

      candidates.push({ element, currentText });
    };

    root.querySelectorAll?.(selectors)?.forEach(visit);
    queryDeep(selectors, root).forEach(visit);

    candidates.forEach(({ element, currentText }) => {
      const hasMatchingDescendant = candidates.some(
        (other) => other.element !== element && element.contains(other.element)
      );
      if (hasMatchingDescendant) return;

      const nextText = transformFn(currentText);
      if (!nextText) return;

      setTextIfChanged(element, nextText);
    });
  }

  function setOptionLabel(option, nextText) {
    option.textContent = nextText;
    option.label = nextText;
    option.setAttribute(DATA_ATTR, 'true');
  }

  function getActiveVariantId() {
    const variantInput = document.querySelector('.product-form.informationSection input[name="id"]');
    return variantInput?.value || variantInput?.getAttribute('value') || '';
  }

  function queryShadowTargets(widget, selector) {
    const shadowRoot = widget?.shadowRoot;
    if (!shadowRoot) return [];

    return Array.from(shadowRoot.querySelectorAll(selector));
  }

  function queryWidgetDeep(widget, selector) {
    const scope = widget?.shadowRoot || widget;
    if (!scope) return [];

    return queryDeep(selector, scope);
  }

  function ensurePdpObservers(widget) {
    if (!widget?.shadowRoot) return;

    observeShadowRoot(widget.shadowRoot, schedulePdpUpdate, state.pdpObservers);

    queryWidgetDeep(widget, 'rc-selling-plans').forEach((host) => {
      if (host.shadowRoot) {
        observeShadowRoot(host.shadowRoot, schedulePdpUpdate, state.pdpObservers);
      }
    });
  }

  function queryBadgeTargets(widget) {
    const selectors = [
      '#rc-subscription-badge',
      '[part*="rc-purchase-option__badge"]',
      '.rc-purchase-option__badge',
      '[part*="rc-subscription-badge"]',
      '.rc-subscription-badge',
    ];
    const seen = new Set();
    const elements = [];

    selectors.forEach((selector) => {
      queryShadowTargets(widget, selector).forEach((element) => {
        if (seen.has(element)) return;
        seen.add(element);
        elements.push(element);
      });
    });

    return elements;
  }

  function updateStickySubscriptionPrice(formattedPrice) {
    const priceDiv = document.querySelector('.sticky-details p.price');
    if (!priceDiv || !formattedPrice) return;

    priceDiv.textContent = `Subscription: ${formattedPrice}`;

    if (typeof handlePriceSelection === 'function') {
      handlePriceSelection('subscription', formattedPrice);
    }
  }

  function updateRechargeWidget(config) {
    const widget = document.querySelector('recharge-subscription-widget');
    if (!widget || !config?.enabled) return;

    const variantId = getActiveVariantId();
    let pricing = getPricing(config, variantId);

    if (!pricing && variantId) {
      const subscriptionEl = queryShadowTargets(widget, '[part*="rc-purchase-option__discounted-price"]')[0];
      const subscriptionCents = parseMoneyToCents(subscriptionEl?.textContent);
      const retailInput = document.querySelector('.product-form.informationSection input[name="id"]');
      const retailCents = parseInt(retailInput?.dataset?.retailPrice, 10) || 0;
      pricing = calculatePricing(
        retailCents,
        subscriptionCents,
        config.extraDiscountPercent,
        config.laborDayActive,
        config.laborDayDiscountPercent
      );
      storePricing(config, variantId, pricing);
    }

    if (!pricing?.saleCents) return;

    const formattedSalePrice = formatMoney(pricing.saleCents);
    const badgeText = pricing.savingsPercent > 0 ? `Save up to ${formatSavingsPercent(pricing.savingsPercent)}%` : '';

    queryBadgeTargets(widget).forEach((badge) => {
      if (badgeText) setTextIfChanged(badge, badgeText);
    });

    queryShadowTargets(widget, '[part*="rc-purchase-option__discounted-price"]').forEach((priceEl) => {
      setTextIfChanged(priceEl, formattedSalePrice);
    });

    updatePdpSellingPlanCopy(widget, pricing.savingsPercent);
    schedulePdpCopyRefresh();

    const selectedRadio = widget.shadowRoot?.querySelector('input[type="radio"][value="subscription"]:checked');
    if (selectedRadio) {
      updateStickySubscriptionPrice(formattedSalePrice);
    }
  }

  function findOneTimeLabelElements(widget) {
    return queryShadowTargets(widget, 'input[type="radio"][value="onetime"]')
      .map((radio) => radio.closest('[part="rc-purchase-option__label"]'))
      .filter(Boolean);
  }

  function updateOneTimeBadge(label, active, savingsPercent) {
    let badge = label.querySelector('.labor-day-onetime-badge');

    if (!active || !(savingsPercent > 0)) {
      if (badge) badge.remove();
      return;
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'rc-purchase-option__badge labor-day-onetime-badge';
      badge.setAttribute('part', 'rc-purchase-option__badge');
      label.appendChild(badge);
    }

    const text = `Save up to ${formatSavingsPercent(savingsPercent)}%`;
    if (badge.textContent !== text) {
      badge.textContent = text;
    }
  }

  function updateOneTimePriceInWidget(config) {
    const widget = document.querySelector('recharge-subscription-widget');
    if (!widget) return;

    const labels = findOneTimeLabelElements(widget);
    if (labels.length === 0) return;

    const active = Boolean(config?.enabled && config.laborDayActive && config.laborDayDiscountPercent > 0);

    if (!active) {
      labels.forEach((label) => {
        updateOneTimeBadge(label, false, 0);
        label.querySelectorAll('[part="rc-purchase-option__price"]').forEach((priceEl) => {
          const original = priceEl.getAttribute(ORIGINAL_ONE_TIME_PRICE_ATTR);
          if (original && priceEl.innerHTML !== original) {
            priceEl.innerHTML = original;
          }
        });
      });
      return;
    }

    const variantId = getActiveVariantId();
    let retailCents = (variantId && config.variants[String(variantId)]?.retailCents) || 0;

    if (!retailCents) {
      const retailInput = document.querySelector('.product-form.informationSection input[name="id"]');
      retailCents = parseInt(retailInput?.dataset?.retailPrice, 10) || 0;
    }

    if (!retailCents) return;

    const discountCents = Math.floor((retailCents * config.laborDayDiscountPercent) / 100);
    const saleCents = retailCents - discountCents;
    if (saleCents <= 0 || saleCents >= retailCents) return;

    const savingsPercent = Math.round(((retailCents - saleCents) * 100) / retailCents);
    const nextHtml = `<s class="rc-price-compare" style="text-decoration:line-through;opacity:.6;margin-right:.35em;">${formatMoney(retailCents)}</s>${formatMoney(saleCents)}`;

    labels.forEach((label) => {
      updateOneTimeBadge(label, true, savingsPercent);
      label.querySelectorAll('[part="rc-purchase-option__price"]').forEach((priceEl) => {
        if (!priceEl.hasAttribute(ORIGINAL_ONE_TIME_PRICE_ATTR)) {
          priceEl.setAttribute(ORIGINAL_ONE_TIME_PRICE_ATTR, priceEl.innerHTML);
        }
        if (priceEl.innerHTML !== nextHtml) {
          priceEl.innerHTML = nextHtml;
        }
      });
    });
  }

  function walkShadowTrees(root, visit) {
    if (!root) return;

    visit(root);

    root.querySelectorAll?.('*').forEach((element) => {
      if (element.shadowRoot) {
        walkShadowTrees(element.shadowRoot, visit);
      }
    });
  }

  function queryDeep(selector, scope) {
    const matches = [];
    const seen = new Set();

    walkShadowTrees(scope || document.documentElement, (root) => {
      root.querySelectorAll?.(selector)?.forEach((element) => {
        if (seen.has(element)) return;
        seen.add(element);
        matches.push(element);
      });
    });

    return matches;
  }

  function findDrawerRoots() {
    const roots = new Set();

    walkShadowTrees(document.documentElement, (root) => {
      if (
        root.querySelector?.('[part="money discount"], [part*="money discount"], [part="money-discount"]') ||
        root.querySelector?.('[class*="text-storefront-drawer-accent"]') ||
        root.querySelector?.('recharge-storefront-drawer')
      ) {
        roots.add(root);
      }
    });

    queryDeep('recharge-storefront-drawer').forEach((host) => {
      if (host.shadowRoot) roots.add(host.shadowRoot);
      roots.add(host);
    });

    return Array.from(roots);
  }

  function getDrawerVariantId(root) {
    const variantEl =
      root.querySelector?.('[data-variant-id], [data-variantid], [data-product-variant-id], input[name="id"]') ||
      root.closest?.('[data-variant-id], [data-variantid], [data-product-variant-id]');

    return (
      variantEl?.dataset?.variantId ||
      variantEl?.dataset?.variantid ||
      variantEl?.dataset?.productVariantId ||
      variantEl?.value ||
      ''
    );
  }

  function updateDrawerHeaderCopy(root, savingsPercent) {
    if (savingsPercent <= 0) return;

    const updateMatch = (element) => {
      if (element.closest?.('.cart-item__subscribe')) return;

      const currentText = element.textContent?.trim();
      if (!currentText || !/Subscribe\s*&\s*save/i.test(currentText)) return;

      const nextText = replacePercentInText(currentText, savingsPercent);
      if (nextText) setTextIfChanged(element, nextText);
    };

    root.querySelectorAll?.('h1, h2, h3, button, p, span').forEach(updateMatch);
    queryDeep('h1, h2, h3, button, p, span', root).forEach(updateMatch);
  }

  function formatDrawerBenefitsSaveText(savingsPercent) {
    return `Save ${formatSavingsPercent(savingsPercent)}% on first order, 20% off on subsequent orders`;
  }

  function updateDrawerBenefitsCopy(root, savingsPercent) {
    if (savingsPercent <= 0) return;

    const nextText = formatDrawerBenefitsSaveText(savingsPercent);
    const isBenefitsSaveLine = (text) =>
      !DRAWER_BENEFITS_COMPLETE_PATTERN.test(text) && DRAWER_BENEFITS_SAVE_PATTERN.test(text);

    updateDeepestDrawerText(
      root,
      'li span, li p, li, [role="listitem"] span, [role="listitem"] p, [role="listitem"]',
      isBenefitsSaveLine,
      () => nextText
    );
  }

  function updateDrawerSellingPlanCopy(root, savingsPercent) {
    if (savingsPercent <= 0) return;

    const selects = [];
    const seenSelects = new Set();

    const collectSelect = (select) => {
      if (!select || seenSelects.has(select)) return;
      if (select.name !== 'selling_plan' && !/^selling_plan/i.test(select.id || '')) return;

      seenSelects.add(select);
      selects.push(select);
    };

    root.querySelectorAll?.('select[name="selling_plan"], select[id^="selling_plan"]').forEach(collectSelect);
    queryDeep('select[name="selling_plan"], select[id^="selling_plan"]', root).forEach(collectSelect);

    selects.forEach((select) => updateSellingPlanOptionsInSelect(select, savingsPercent));

    updateDeepestDrawerText(
      root,
      'p, span, label, div',
      isSubscriptionPlanLabel,
      (text) => replacePercentInText(text, savingsPercent)
    );
  }

  function updatePdpSellingPlanCopy(widget, savingsPercent) {
    if (savingsPercent <= 0 || !widget) return;

    const selectSelectors =
      'select[name="rc_plan"], select[part="rc-plans-select"], .rc-plans-dropdown__select';

    queryWidgetDeep(widget, selectSelectors).forEach((select) => {
      updateSellingPlanOptionsInSelect(select, savingsPercent);
    });

    queryWidgetDeep(widget, '[part="rc-plans-button__discount"], .rc-plans-button__discount').forEach(
      (element) => {
        const currentText = element.textContent?.trim();
        if (!currentText || !PERCENT_PATTERN.test(currentText) || !/save/i.test(currentText)) return;

        const nextText = replacePercentInText(currentText, savingsPercent);
        if (nextText) setTextIfChanged(element, nextText);
      }
    );
  }

  function schedulePdpCopyRefresh() {
    window.clearTimeout(state.pdpCopyTimer);
    state.pdpCopyTimer = window.setTimeout(() => {
      const widget = document.querySelector('recharge-subscription-widget');
      if (!widget || !state.config?.enabled) return;

      mergeConfig();
      const variantId = getActiveVariantId();
      const pricing = getPricing(state.config, variantId);
      if (!pricing?.savingsPercent) return;

      updatePdpSellingPlanCopy(widget, pricing.savingsPercent);
    }, 80);

    [250, 550, 900, 1400].forEach((delay) => {
      window.setTimeout(() => {
        const widget = document.querySelector('recharge-subscription-widget');
        if (!widget || !state.config?.enabled) return;

        mergeConfig();
        const variantId = getActiveVariantId();
        let pricing = getPricing(state.config, variantId);
        if (!pricing?.savingsPercent && variantId) {
          const subscriptionEl = queryWidgetDeep(widget, '[part*="rc-purchase-option__discounted-price"]')[0];
          const subscriptionCents = parseMoneyToCents(subscriptionEl?.textContent);
          const retailInput = document.querySelector('.product-form.informationSection input[name="id"]');
          const retailCents = parseInt(retailInput?.dataset?.retailPrice, 10) || 0;
          pricing = calculatePricing(
            retailCents,
            subscriptionCents,
            state.config.extraDiscountPercent,
            state.config.laborDayActive,
            state.config.laborDayDiscountPercent
          );
        }
        if (!pricing?.savingsPercent) return;

        updatePdpSellingPlanCopy(widget, pricing.savingsPercent);
      }, delay);
    });
  }

  function resolveDrawerSavingsPercent(root, config) {
    const priceSelectors =
      '[part="money discount"], [part*="money discount"], [part="money-discount"], [part*="money-discount"]';
    const priceElement = collectElements(priceSelectors, root)[0];
    const pricing = priceElement
      ? resolvePricingForPriceElement(priceElement, config)
      : getPricingFromActiveForm(config, getDrawerVariantId(root));

    return pricing?.savingsPercent > 0 ? pricing.savingsPercent : 0;
  }

  function applyDrawerCopyUpdates(root, config) {
    if (!config?.enabled) return;

    const savingsPercent = resolveDrawerSavingsPercent(root, config);
    if (!savingsPercent) return;

    updateDrawerHeaderCopy(root, savingsPercent);
    updateDrawerBenefitsCopy(root, savingsPercent);
    updateDrawerSellingPlanCopy(root, savingsPercent);
  }

  function scheduleDrawerCopyRefresh() {
    window.clearTimeout(state.drawerCopyTimer);
    state.drawerCopyTimer = window.setTimeout(() => {
      if (!state.config?.enabled) return;

      mergeConfig();
      const roots = findDrawerRoots();
      const targets = roots.length > 0 ? roots : [document.documentElement];
      targets.forEach((root) => applyDrawerCopyUpdates(root, state.config));
    }, 80);

    [250, 550, 900].forEach((delay) => {
      window.setTimeout(() => {
        if (!state.config?.enabled) return;

        mergeConfig();
        const roots = findDrawerRoots();
        const targets = roots.length > 0 ? roots : [document.documentElement];
        targets.forEach((root) => applyDrawerCopyUpdates(root, state.config));
      }, delay);
    });
  }

  function storePricing(config, variantId, pricing) {
    if (!config || !variantId || !pricing?.saleCents) return;
    config.variants[String(variantId)] = pricing;
  }

  function getPricingFromActiveForm(config, drawerVariantId) {
    const variantInput = document.querySelector('.product-form.informationSection input[name="id"]');
    if (!variantInput) return null;

    const formVariantId = variantInput.value || variantInput.getAttribute('value') || '';
    if (drawerVariantId && formVariantId && String(formVariantId) !== String(drawerVariantId)) {
      return null;
    }

    const retailCents = parseInt(variantInput.dataset.retailPrice, 10) || 0;
    const subscriptionCents = parseInt(variantInput.dataset.subscriptionPrice, 10) || 0;
    if (!subscriptionCents) return null;

    const pricing = calculatePricing(
      retailCents,
      subscriptionCents,
      config.extraDiscountPercent,
      config.laborDayActive,
      config.laborDayDiscountPercent
    );
    storeDrawerPricing(formVariantId, pricing);
    return pricing;
  }

  function applyDrawerPricing(root, config, immediate) {
    if (!config?.enabled) return;
    if (!immediate) {
      if (state.drawerUpdateLocked) return;
      state.drawerUpdateLocked = true;
    }

    const priceSelectors =
      '[part="money discount"], [part*="money discount"], [part="money-discount"], [part*="money-discount"]';
    const priceElements = collectElements(priceSelectors, root);
    let headerPricing = null;

    priceElements.forEach((priceEl) => {
      const pricing = resolvePricingForPriceElement(priceEl, config);
      if (!pricing?.saleCents) return;

      if (!headerPricing) headerPricing = pricing;

      const quantity = getLineQuantity(priceEl, pricing.retailCents);
      const lineSaleCents = pricing.saleCents * quantity;
      const nextText = formatMoney(lineSaleCents);
      const previousQty = parseInt(priceEl.getAttribute(LINE_QTY_ATTR), 10);

      rememberOriginalMoney(priceEl, ORIGINAL_SUBSCRIPTION_ATTR, pricing.subscriptionCents);
      rememberOriginalMoney(priceEl, ORIGINAL_RETAIL_ATTR, pricing.retailCents);
      priceEl.setAttribute(LINE_QTY_ATTR, String(quantity));

      if (priceEl.textContent.trim() !== nextText || previousQty !== quantity) {
        priceEl.textContent = nextText;
        priceEl.setAttribute(DATA_ATTR, 'true');
      }
    });

    if (!headerPricing) {
      if (!immediate) {
        window.setTimeout(() => {
          state.drawerUpdateLocked = false;
        }, 150);
      }
      return;
    }

    const savingText = `Saving ${formatSavingsPercent(headerPricing.savingsPercent)}%`;

    root.querySelectorAll?.('[class*="text-storefront-drawer-accent"]').forEach((element) => {
      const currentText = element.textContent?.trim();
      if (!currentText || !/^Saving\s+\d+(?:\.\d{1,2})?%$/i.test(currentText)) return;
      setTextIfChanged(element, savingText);
    });

    queryDeep('[class*="text-storefront-drawer-accent"]', root).forEach((element) => {
      const currentText = element.textContent?.trim();
      if (!currentText || !/^Saving\s+\d+(?:\.\d{1,2})?%$/i.test(currentText)) return;
      setTextIfChanged(element, savingText);
    });

    updateDrawerHeaderCopy(root, headerPricing.savingsPercent);
    updateDrawerBenefitsCopy(root, headerPricing.savingsPercent);
    updateDrawerSellingPlanCopy(root, headerPricing.savingsPercent);

    if (!immediate) {
      scheduleDrawerCopyRefresh();

      window.setTimeout(() => {
        state.drawerUpdateLocked = false;
      }, 150);
    }
  }

  function updateRechargeDrawer(config) {
    if (!config?.enabled) return;
    if (state.drawerUpdateLocked) {
      window.clearTimeout(state.drawerTimer);
      state.drawerTimer = window.setTimeout(() => updateRechargeDrawer(config), 180);
      return;
    }

    mergeConfig();

    const roots = findDrawerRoots();
    const targets = roots.length > 0 ? roots : [document.documentElement];

    targets.forEach((root) => {
      applyDrawerPricing(root, config);
    });
  }

  function observeShadowRoot(root, callback, observerMap) {
    if (!root || observerMap.has(root)) return;

    callback();
    const observer = new MutationObserver(callback);
    observer.observe(root, {
      childList: true,
      subtree: true,
    });
    observerMap.set(root, observer);
  }

  function schedulePdpUpdate() {
    window.clearTimeout(state.updateTimer);
    state.updateTimer = window.setTimeout(() => {
      const widget = document.querySelector('recharge-subscription-widget');
      if (widget) ensurePdpObservers(widget);
      if (state.config) updateRechargeWidget(state.config);
      updateOneTimePriceInWidget(state.config);
    }, 50);
  }

  function applyDrawerUpdatesImmediate() {
    if (state.drawerImmediateRunning || !state.config?.enabled) return;

    const roots = findDrawerRoots();
    if (roots.length === 0) return;

    state.drawerImmediateRunning = true;
    try {
      roots.forEach((root) => {
        applyDrawerPricing(root, state.config, true);
        applyDrawerCopyUpdates(root, state.config);
      });
    } catch (error) {
      console.error('Subscription sale drawer immediate update failed.', error);
    } finally {
      state.drawerImmediateRunning = false;
    }
  }

  function scheduleDrawerUpdate() {
    applyDrawerUpdatesImmediate();

    window.clearTimeout(state.drawerTimer);
    state.drawerTimer = window.setTimeout(() => {
      if (state.config) updateRechargeDrawer(state.config);
    }, 50);
  }

  function scheduleQtyDrawerUpdate() {
    window.clearTimeout(state.qtyUpdateTimer);
    [120, 350, 650].forEach((delay) => {
      window.setTimeout(() => {
        if (state.config) updateRechargeDrawer(state.config);
      }, delay);
    });
  }

  function isStepperControlClick(target) {
    if (!(target instanceof Element)) return false;

    if (target.closest?.('[part*="quantity"], [class*="quantity"], [class*="qty"]')) return true;
    if (target.closest?.('input[type="number"]')) return true;

    const button = target.closest?.('button');
    if (!button?.parentElement) return false;

    const stepperButtons = button.parentElement.querySelectorAll?.('button');
    return Boolean(stepperButtons && stepperButtons.length === 2);
  }

  function initPdpObserver() {
    const bootWidgetObserver = () => {
      const widget = document.querySelector('recharge-subscription-widget');
      if (!widget) return;

      if (widget.shadowRoot) {
        ensurePdpObservers(widget);
        schedulePdpUpdate();
        return;
      }

      const hostObserver = new MutationObserver(() => {
        if (widget.shadowRoot) {
          hostObserver.disconnect();
          bootWidgetObserver();
        }
      });
      hostObserver.observe(widget, { childList: true });
    };

    bootWidgetObserver();
    window.setTimeout(bootWidgetObserver, 500);
    window.setTimeout(bootWidgetObserver, 1500);

    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)) return;
        if (target.name !== 'rc_plan') return;

        schedulePdpUpdate();
        schedulePdpCopyRefresh();
      },
      true
    );

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (target.matches('.product-form.informationSection input[name="id"]')) {
        state.lockedDrawerPricing = null;
        mergeConfig();
        schedulePdpUpdate();
        scheduleDrawerUpdate();
      }
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        state.lockedDrawerPricing = null;
        mergeConfig();
        schedulePdpUpdate();
        scheduleDrawerUpdate();
      });
    }
  }

  function initDrawerObserver() {
    const bootDrawerObserver = () => {
      findDrawerRoots().forEach((root) => {
        observeShadowRoot(root, scheduleDrawerUpdate, state.drawerObservers);
      });
      scheduleDrawerUpdate();
    };

    bootDrawerObserver();

    const documentObserver = new MutationObserver(bootDrawerObserver);
    documentObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    document.addEventListener('recharge:storefront-drawer:open', () => {
      state.lockedDrawerPricing = null;
      mergeConfig();
      bootDrawerObserver();
      scheduleDrawerUpdate();
      scheduleDrawerCopyRefresh();
      window.setTimeout(scheduleDrawerUpdate, 200);
      window.setTimeout(scheduleDrawerUpdate, 500);
      window.setTimeout(scheduleDrawerCopyRefresh, 200);
      window.setTimeout(scheduleDrawerCopyRefresh, 500);
    });

    document.addEventListener(
      'click',
      (event) => {
        if (isStepperControlClick(event.target)) {
          scheduleQtyDrawerUpdate();
          return;
        }

        if (event.target instanceof Element && event.target.closest?.('recharge-storefront-drawer')) {
          window.setTimeout(scheduleDrawerUpdate, 200);
        }
      },
      true
    );

    document.addEventListener(
      'input',
      (event) => {
        if (event.target instanceof HTMLInputElement && event.target.type === 'number') {
          scheduleQtyDrawerUpdate();
        }
      },
      true
    );
  }

  function initSubscriptionSalePricing() {
    state.config = parseConfig();
    if (!state.config) return;

    window.MenoSubscriptionSalePricing = state.config;
    initPdpObserver();
    initDrawerObserver();
    schedulePdpUpdate();
    scheduleDrawerUpdate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSubscriptionSalePricing);
  } else {
    initSubscriptionSalePricing();
  }
})();
